"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Support() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const tile1 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const tile2 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const tile3 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const tile4 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const cohortCells = Array.from({
    length: 96
  }, (_, i) => {
    const col = i % 16;
    const row = Math.floor(i / 16);
    const intensity = Math.max(0, 1 - (col * 0.06 + row * 0.05));
    return {
      id: i,
      intensity
    };
  });
  const line1Points = "0,80 50,72 100,58 150,62 200,40 250,32 300,20";
  const line2Points = "0,90 50,85 100,75 150,68 200,60 250,55 300,45";
  const line3Points = "0,70 50,68 100,65 150,50 200,48 250,38 300,30";
  return <section data-section-id="16720" id="the-support-system" className="bg-background text-foreground ![padding:0px_40px_128px_40px]">
      <div className="mx-auto" style={{
      maxWidth: "1320px"
    }}>
        <div ref={header.ref} className="mx-auto text-center mb-16 md:mb-20 transition-all duration-700 ease-out" style={{
        maxWidth: "820px",
        transform: header.isVisible ? "translateY(0)" : "translateY(24px)",
        opacity: header.isVisible ? 1 : 0
      }}>
          <h2 className="font-default font-bold text-primary" style={{
          fontSize: "clamp(2.125rem, 3.5vw + 0.5rem, 3.75rem)",
          letterSpacing: "-0.03em",
          lineHeight: 1.04
        }}>
            Precision in{" "}
            <span className="text-primary">Patient Positioning</span>.
          </h2>
          <p className="mt-6 font-default text-foreground" style={{
          fontSize: "clamp(1.0625rem, 0.4vw + 0.95rem, 1.1875rem)",
          lineHeight: 1.55,
          letterSpacing: "-0.005em"
        }}>The NoMoCo Pillow Neuroimaging Support System provides clinical-grade stability for imaging centers. Our research-backed design ensures consistent, repeatable patient positioning to improve diagnostic clarity and reduce motion artifacts.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          <div ref={tile1.ref} data-index={0} className="md:col-span-8 md:row-span-2 group relative overflow-hidden transition-all duration-700 ease-out hover:-translate-y-1 shadow-md rounded-md" style={{
          backgroundColor: "hsl(215, 30%, 96%)",
          padding: "clamp(2rem, 3vw, 3rem)",
          transform: tile1.isVisible ? "translateY(0)" : "translateY(24px)",
          opacity: tile1.isVisible ? 1 : 0,
          transitionProperty: "transform, opacity, border-color",
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
          transitionDuration: "620ms, 620ms, 280ms"
        }}>
            <div className="flex flex-col h-full">
              <div className="mb-6">
                <div className="font-default uppercase mb-4 text-primary" style={{
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "0.15em"
              }}>
                  Clinical Stability
                </div>
                <h3 className="font-default font-bold text-primary" style={{
                fontSize: "clamp(1.375rem, 1.2vw + 0.9rem, 1.875rem)",
                letterSpacing: "-0.02em",
                lineHeight: 1.15
              }}>
                  Optimized Patient Support
                </h3>
                <p className="mt-3 font-default text-foreground" style={{
                fontSize: "1rem",
                lineHeight: 1.6
              }}>
                  Engineered for university imaging centers and hospitals, our system minimizes patient movement, ensuring high-fidelity scans every time.
                </p>
              </div>

              <div className="mt-auto">
                <img src="https://nomocopillow.com/wp-content/uploads/2026/06/bghero.png" alt="Person positioned in a brain imaging scanner using the NoMoCo Pillow support system for head stabilization." width="776" height="443" />
                <div className="flex items-center justify-between mt-4">
                  <span className="font-default uppercase text-accent" style={{
                  fontSize: "0.6875rem",
                  letterSpacing: "0.04em"
                }}>
                    Stability
                  </span>
                  <span className="font-default uppercase text-accent" style={{
                  fontSize: "0.6875rem",
                  letterSpacing: "0.04em"
                }}>
                    Precision
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div ref={tile2.ref} data-index={1} className="md:col-span-4 group transition-all duration-700 ease-out hover:-translate-y-1 shadow-md rounded-md" style={{
          backgroundColor: "hsl(215, 30%, 96%)",
          padding: "clamp(1.75rem, 2vw, 2.25rem)",
          transform: tile2.isVisible ? "translateY(0)" : "translateY(24px)",
          opacity: tile2.isVisible ? 1 : 0,
          transitionProperty: "transform, opacity, border-color",
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
          transitionDuration: "620ms, 620ms, 280ms",
          transitionDelay: "80ms"
        }}>
            <div className="flex flex-col h-full">
              <div>
                <div className="font-default uppercase mb-3 text-primary" style={{
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "0.15em"
              }}>
                  Workflow
                </div>
                <h3 className="font-default font-bold text-primary" style={{
                fontSize: "clamp(1.375rem, 1.2vw + 0.9rem, 1.875rem)",
                letterSpacing: "-0.02em",
                lineHeight: 1.15
              }}>
                  Efficiency
                </h3>
                <p className="mt-2 font-default text-foreground" style={{
                fontSize: "0.9375rem",
                lineHeight: 1.55
              }}>
                  Streamline your imaging workflow with intuitive, easy-to-clean support components.
                </p>
              </div>

              <div className="mt-6 flex flex-col gap-2 items-center">
                {[100, 78, 52, 31, 14].map((width, i) => <div key={i} data-index={i} className="flex items-center justify-between px-3 transition-all duration-700 rounded-sm" style={{
                width: tile2.isVisible ? `${width}%` : "0%",
                height: "28px",
                backgroundColor: i === 4 ? "hsl(228, 69%, 40%)" : `hsl(215, 27%, ${60 + i * 6}%)`,
                transitionDelay: `${300 + i * 120}ms`,
                transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
              }}>
                    <span className="font-default text-white" style={{
                  fontSize: "0.6875rem",
                  fontWeight: 500
                }}>
                      {["Prep", "Align", "Scan", "Review", "Done"][i]}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>

          <div ref={tile3.ref} data-index={2} className="md:col-span-4 group transition-all duration-700 ease-out hover:-translate-y-1 shadow-md rounded-md" style={{
          backgroundColor: "hsl(215, 30%, 96%)",
          padding: "clamp(1.75rem, 2vw, 2.25rem)",
          transform: tile3.isVisible ? "translateY(0)" : "translateY(24px)",
          opacity: tile3.isVisible ? 1 : 0,
          transitionProperty: "transform, opacity, border-color",
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
          transitionDuration: "620ms, 620ms, 280ms",
          transitionDelay: "160ms"
        }}>
            <div className="flex flex-col h-full">
              <div>
                <div className="font-default uppercase mb-3 text-primary" style={{
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "0.15em"
              }}>
                  Reliability
                </div>
                <h3 className="font-default font-bold text-primary" style={{
                fontSize: "clamp(1.375rem, 1.2vw + 0.9rem, 1.875rem)",
                letterSpacing: "-0.02em",
                lineHeight: 1.15
              }}>
                  Consistent Results
                </h3>
                <p className="mt-2 font-default text-foreground" style={{
                fontSize: "0.9375rem",
                lineHeight: 1.55
              }}>
                  Reduce repeat scans and improve patient comfort with our specialized support system.
                </p>
              </div>

              <div className="mt-6 flex-1 flex items-end">
                <svg viewBox="0 0 300 100" className="w-full" style={{
                height: "auto"
              }} preserveAspectRatio="none">
                  {[20, 50, 80].map((y, i) => <line key={i} data-index={i} x1="0" y1={y} x2="300" y2={y} stroke="hsl(215, 27%, 62% / 0.3)" strokeWidth="1" />)}
                  <polyline points={line1Points} fill="none" stroke="hsl(228, 69%, 40%)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{
                  strokeDasharray: 600,
                  strokeDashoffset: tile3.isVisible ? 0 : 600,
                  transition: "stroke-dashoffset 1400ms cubic-bezier(0.22, 1, 0.36, 1)",
                  transitionDelay: "300ms"
                }} />
                </svg>
              </div>
            </div>
          </div>

          <div ref={tile4.ref} data-index={3} className="md:col-span-12 group transition-all duration-700 ease-out hover:-translate-y-1 shadow-md rounded-md" style={{
          backgroundColor: "hsl(215, 30%, 96%)",
          padding: "clamp(1.75rem, 2.5vw, 2.5rem)",
          transform: tile4.isVisible ? "translateY(0)" : "translateY(24px)",
          opacity: tile4.isVisible ? 1 : 0,
          transitionProperty: "transform, opacity, border-color",
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
          transitionDuration: "620ms, 620ms, 280ms",
          transitionDelay: "240ms"
        }}>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div className="md:max-w-md">
                <div className="font-default uppercase mb-3 text-primary" style={{
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "0.15em"
              }}>
                  Durability
                </div>
                <h3 className="font-default font-bold text-primary" style={{
                fontSize: "clamp(1.375rem, 1.2vw + 0.9rem, 1.875rem)",
                letterSpacing: "-0.02em",
                lineHeight: 1.15
              }}>
                  Medical Grade Materials
                </h3>
                <p className="mt-2 font-default text-foreground" style={{
                fontSize: "1rem",
                lineHeight: 1.6
              }}>
                  Built for the rigors of clinical environments, our vinyl-coated kits are designed for longevity and easy sanitation.
                </p>
              </div>

              <div className="md:flex-1 md:max-w-2xl">
                <img src="https://nomocowp.10web.cloud/wp-content/uploads/2026/06/nomocopillowimage.png" alt="" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}